import React, { useState, useEffect } from 'react';
import { PlusCircle, Edit, Trash2, Save, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../../components/Layout';
import { api } from '../../../api';
import siteContentData from '../../../siteContentData.json';

interface plansItem {
  _id?: string;
  id: string;
  name: string;
  price: string;
  period: string;
  players: string;
  engagement: string;
  features: string[];
  popular: boolean;
}

const Adminplans: React.FC = () => {
  const [plans, setplans] = useState<plansItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingItem, setEditingItem] = useState<plansItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    loadplans();
  }, []);

  const loadplans = async () => {
    try {
      setIsLoading(true);
      // In a real app, you would fetch from API:
      // const response = await api.get('/admin/content/plans');
      // setplans(response.data.plans || []);
      
      // For now using local data:
      setplans(siteContentData.plans || []);
    } catch (error) {
      console.error('Error loading plans:', error);
      toast.error('Failed to load plans.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditingItem(prev => ({
      ...prev!,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setEditingItem(prev => ({
      ...prev!,
      [name]: checked
    }));
  };

  const addFeature = () => {
    if (newFeature.trim() && editingItem) {
      setEditingItem({
        ...editingItem,
        features: [...editingItem.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index: number) => {
    if (editingItem) {
      const updatedFeatures = [...editingItem.features];
      updatedFeatures.splice(index, 1);
      setEditingItem({
        ...editingItem,
        features: updatedFeatures
      });
    }
  };

  const openCreateModal = () => {
    setEditingItem({
      id: '',
      name: '',
      price: '',
      period: '/mois',
      players: '',
      engagement: '',
      features: [],
      popular: false
    });
    setIsModalOpen(true);
  };

  const openEditModal = (item: plansItem) => {
    setEditingItem({ ...item });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem) return;

    setIsSaving(true);
    try {
      if (editingItem._id) {
        // await api.put(`/admin/content/plans/${editingItem._id}`, editingItem);
        toast.success('Plan updated successfully!');
      } else {
        // await api.post('/admin/content/plans', editingItem);
        toast.success('Plan created successfully!');
      }
      closeModal();
      loadplans();
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error('Failed to save plan.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this plan?')) {
      try {
        // await api.delete(`/admin/content/plans/${id}`);
        toast.success('Plan deleted successfully!');
        loadplans();
      } catch (error) {
        console.error('Error deleting plan:', error);
        toast.error('Failed to delete plan.');
      }
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFB639]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Manage Plans</h1>
          <button
            onClick={openCreateModal}
            className="flex items-center px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Add Plan
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {plans.length === 0 ? (
            <p className="text-gray-500 text-center">No plans found. Add one!</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Players</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Engagement</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Popular</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {plans.map((item) => (
                    <tr key={item._id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.price}{item.period}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.players}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.engagement}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.popular ? 'Yes' : 'No'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => openEditModal(item)}
                          className="text-[#FFB639] hover:text-black mr-4"
                          title="Edit"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(item._id!)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit/Create Modal */}
      {isModalOpen && editingItem && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingItem._id ? 'Edit Plan' : 'Add New Plan'}
              </h2>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  name="name"
                  value={editingItem.name}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#FFB639] focus:border-[#FFB639]"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Price</label>
                  <input
                    type="text"
                    name="price"
                    value={editingItem.price}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#FFB639] focus:border-[#FFB639]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Period</label>
                  <input
                    type="text"
                    name="period"
                    value={editingItem.period}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#FFB639] focus:border-[#FFB639]"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Players</label>
                  <input
                    type="text"
                    name="players"
                    value={editingItem.players}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#FFB639] focus:border-[#FFB639]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Engagement</label>
                  <input
                    type="text"
                    name="engagement"
                    value={editingItem.engagement}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#FFB639] focus:border-[#FFB639]"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="popular"
                    checked={editingItem.popular}
                    onChange={handleCheckboxChange}
                    className="h-4 w-4 text-[#FFB639] focus:ring-[#FFB639] border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Popular Plan</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Features</label>
                <div className="mt-1 space-y-2">
                  {editingItem.features.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <span className="flex-grow bg-gray-100 px-3 py-2 rounded-md">{feature}</span>
                      <button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="ml-2 text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <div className="flex">
                    <input
                      type="text"
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      className="flex-grow border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#FFB639] focus:border-[#FFB639]"
                      placeholder="Add new feature"
                    />
                    <button
                      type="button"
                      onClick={addFeature}
                      className="ml-2 px-3 py-2 bg-[#FFB639] text-white rounded-md hover:bg-[#e5a333]"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FFB639]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#FFB639] hover:bg-[#e5a333] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FFB639] disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Adminplans;